import apiClient from '../apiClient';
import type { ApiResponse, Machine, PaginationMeta } from '@/types';
import { getUrlWithQueryParams } from '@/lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';
import { ReactMutationKeys } from '../reactMutationKeys';

const BASE_URL = '/dashboard/machines';
const ADMIN_URL = '/admin/machines';

interface PaginatedResponse {
  items: Machine[];
  pagination: PaginationMeta;
}

export const getMachines = async (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  status?: string,
  connectionStatus?: string,
  companyId?: string
): Promise<PaginatedResponse> => {
  const url = getUrlWithQueryParams(BASE_URL, {
    page,
    limit,
    search,
    sortBy,
    sortOrder,
    status,
    connectionStatus,
    companyId,
  });
  const { data } = await apiClient.get<ApiResponse<Machine[]>>(url);
  return {
    items: data.data,
    pagination: data.meta!,
  };
};

export const getMachine = async (id: string): Promise<Machine> => {
  const { data } = await apiClient.get<ApiResponse<Machine>>(`${BASE_URL}/${id}`);
  return data.data;
};

export const createMachine = async (machine: Partial<Machine>): Promise<Machine> => {
  const { data } = await apiClient.post<ApiResponse<Machine>>(ADMIN_URL, machine);
  return data.data;
};

export const updateMachine = async (id: string, machine: Partial<Machine>): Promise<Machine> => {
  const { data } = await apiClient.put<ApiResponse<Machine>>(`${ADMIN_URL}/${id}`, machine);
  return data.data;
};

export const deleteMachine = async (id: string): Promise<void> => {
  await apiClient.delete(`${ADMIN_URL}/${id}`);
};

// React Query Hooks

export const useGetMachines = (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  status?: string,
  connectionStatus?: string,
  companyId?: string
) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_MACHINES, page, limit, search, sortBy, sortOrder, status, connectionStatus, companyId],
    queryFn: () => getMachines(page, limit, search, sortBy, sortOrder, status, connectionStatus, companyId),
  });

export const useGetMachine = (id: string) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_MACHINE, id],
    queryFn: () => getMachine(id),
    enabled: !!id,
  });

export const useCreateMachine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (machine: Partial<Machine>) => createMachine(machine),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_MACHINES] });
    },
    mutationKey: [ReactMutationKeys.CREATE_MACHINE],
  });
};

export const useUpdateMachine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...machine }: Partial<Machine> & { id: string }) =>
      updateMachine(id, machine),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_MACHINES] });
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_MACHINE] });
    },
    mutationKey: [ReactMutationKeys.UPDATE_MACHINE],
  });
};

export const useDeleteMachine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMachine(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_MACHINES] });
    },
    mutationKey: [ReactMutationKeys.DELETE_MACHINE],
  });
};
